import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

interface VariableReferenceProps {
  open: boolean;
  onClose: () => void;
}

interface VariableInfo {
  name: string;
  description: string;
  example: string;
}

interface CategoryInfo {
  category: string;
  description: string;
  variables: VariableInfo[];
}

/**
 * VariableReference Component
 * Documentation dialog showing all available template variables
 */
export const VariableReference: React.FC<VariableReferenceProps> = ({ open, onClose }) => {
  const [searchText, setSearchText] = useState('');
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);

  /**
   * Variable reference data organized by category
   */
  const variableCategories: CategoryInfo[] = [
    {
      category: 'User Information',
      description: 'Variables related to user accounts and profiles',
      variables: [
        {
          name: 'userName',
          description: "User's display name",
          example: 'John Smith',
        },
        {
          name: 'userEmail',
          description: "User's email address",
          example: 'john.smith@example.com',
        },
        {
          name: 'newRole',
          description: "User's new role after role change",
          example: 'Editor',
        },
      ],
    },
    {
      category: 'Family Tree',
      description: 'Variables for persons and relationships in the family tree',
      variables: [
        {
          name: 'personName',
          description: 'Name of a person in the family tree',
          example: 'Jane Doe',
        },
        {
          name: 'relationship',
          description: 'Description of a family relationship',
          example: 'Father of Sarah Johnson',
        },
      ],
    },
    {
      category: 'Content & Items',
      description: 'Variables for content items like photos, documents, and memories',
      variables: [
        {
          name: 'itemName',
          description: 'Name or title of a content item',
          example: 'Family Photo Album',
        },
        {
          name: 'contentType',
          description: 'Type of content (photo, document, memory, etc.)',
          example: 'photo',
        },
        {
          name: 'documentName',
          description: 'Name of a shared document',
          example: 'Birth Certificate 1920.pdf',
        },
        {
          name: 'photoCount',
          description: 'Number of photos uploaded or affected',
          example: '5',
        },
      ],
    },
    {
      category: 'Research & Collaboration',
      description: 'Variables for research requests and collaboration',
      variables: [
        {
          name: 'topic',
          description: 'Research topic or subject',
          example: 'German ancestry records',
        },
        {
          name: 'projectName',
          description: 'Name of a collaboration project',
          example: 'Smith Family History Project',
        },
      ],
    },
    {
      category: 'Dates & Times',
      description: 'Variables for dates, times, and durations',
      variables: [
        {
          name: 'daysAway',
          description: 'Time until an event (e.g., birthday, anniversary)',
          example: 'in 3 days',
        },
        {
          name: 'dueDate',
          description: 'Due date for a task or reminder',
          example: 'December 25, 2024',
        },
        {
          name: 'maintenanceTime',
          description: 'Scheduled maintenance time window',
          example: 'Sunday, 2:00 AM - 4:00 AM EST',
        },
      ],
    },
    {
      category: 'Events & Reminders',
      description: 'Variables for events, tasks, and reminders',
      variables: [
        {
          name: 'eventName',
          description: 'Name of an event or anniversary',
          example: 'Wedding',
        },
        {
          name: 'taskName',
          description: 'Name of a task or reminder',
          example: 'Review family tree edits',
        },
      ],
    },
    {
      category: 'Moderation & Reasons',
      description: 'Variables for content moderation and explanations',
      variables: [
        {
          name: 'reason',
          description: 'Reason for approval, rejection, or other action',
          example: 'Contains inappropriate content',
        },
      ],
    },
    {
      category: 'Progress & Metrics',
      description: 'Variables for completion percentages and statistics',
      variables: [
        {
          name: 'percentComplete',
          description: 'Completion percentage for profile or task',
          example: '75',
        },
      ],
    },
    {
      category: 'System & Broadcast',
      description: 'Variables for system messages and admin broadcasts',
      variables: [
        {
          name: 'broadcastTitle',
          description: 'Title of admin broadcast message',
          example: 'Important Announcement',
        },
        {
          name: 'broadcastMessage',
          description: 'Body of admin broadcast message',
          example: 'Please review the updated community guidelines.',
        },
        {
          name: 'updateMessage',
          description: 'System update message',
          example: 'New features have been added to improve your experience.',
        },
        {
          name: 'featureName',
          description: 'Name of a new feature',
          example: 'Advanced Search',
        },
        {
          name: 'featureDescription',
          description: 'Description of a new feature',
          example: 'Find ancestors faster with our new advanced search filters.',
        },
      ],
    },
  ];

  /**
   * Filter categories and variables based on search
   */
  const filteredCategories = variableCategories
    .map((category) => ({
      ...category,
      variables: category.variables.filter((v) => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
          v.name.toLowerCase().includes(search) ||
          v.description.toLowerCase().includes(search) ||
          v.example.toLowerCase().includes(search) ||
          category.category.toLowerCase().includes(search)
        );
      }),
    }))
    .filter((category) => category.variables.length > 0);

  /**
   * Copy variable to clipboard
   */
  const handleCopyVariable = (variableName: string) => {
    const placeholder = `{{${variableName}}}`;
    navigator.clipboard.writeText(placeholder);
    setCopiedVariable(variableName);
    setTimeout(() => setCopiedVariable(null), 2000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Template Variable Reference</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Variables are placeholders in your templates that get replaced with actual values when
            notifications are sent. Use the format <code>{'{{variableName}}'}</code> in your
            template text.
          </Alert>

          <TextField
            placeholder="Search variables..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {filteredCategories.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No variables found matching "{searchText}"
            </Typography>
          </Box>
        ) : (
          filteredCategories.map((category, index) => (
            <Accordion key={category.category} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {category.category}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {category.description}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Variable</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Example</TableCell>
                        <TableCell align="center">Copy</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {category.variables.map((variable) => (
                        <TableRow key={variable.name} hover>
                          <TableCell>
                            <Chip
                              label={`{{${variable.name}}}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{variable.description}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic' }}
                            >
                              {variable.example}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyVariable(variable.name)}
                              color={copiedVariable === variable.name ? 'success' : 'default'}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Usage Tips
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                Variables must be enclosed in double curly braces: <code>{'{{variableName}}'}</code>
              </li>
              <li>Variable names are case-sensitive</li>
              <li>
                You can use multiple variables in a single template
              </li>
              <li>
                If a variable is used in your template, it must be declared in the template's
                variable list
              </li>
              <li>
                Test your templates using the "Test Send" feature to verify variable substitution
              </li>
            </ul>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
